import { Controller, useForm } from "@pankod/refine-react-hook-form";
import { Edit, Box, TextField, Autocomplete, Grid } from "@pankod/refine-mui";
import { IRole, IUser } from "interfaces";
import { useRoles } from "./useRoles";
import { getApiErrorMessage } from "./apiError";

export const UserEdit: React.FC = () => {
  const { roles, isLoading: rolesLoading } = useRoles();
  const {
    refineCore: { formLoading, onFinish },
    saveButtonProps,
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    refineCoreProps: {
      metaData: {
        populate: ["role"],
      },
      errorNotification: (error: any) => ({
        message: getApiErrorMessage(error),
        description: "Revisa los datos e inténtalo de nuevo",
        type: "error",
      }),
    },
  });

  const onSubmit = (data: any) => {
    const payload: Record<string, unknown> = {
      username: data.username,
      email: data.email,
      role: data.role?.id ?? data.role,
    };

    if (data.password) {
      payload.password = data.password;
    }

    onFinish(payload);
  };

  return (
    <Edit
      isLoading={formLoading}
      saveButtonProps={{
        ...saveButtonProps,
        onClick: handleSubmit(onSubmit),
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="role"
              rules={{ required: "Tipo es obligatorio" }}
              defaultValue={null as any}
              render={({ field }) => {
                return (
                  <Autocomplete
                    {...field}
                    options={roles}
                    loading={rolesLoading}
                    onChange={(_, value) => {
                      field.onChange(value);
                    }}
                    getOptionLabel={(item) => {
                      const value = item?.id ?? item;
                      return (
                        roles.find((role) => role.id?.toString() === value?.toString())
                          ?.name ??
                        item?.name ??
                        ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) => {
                      const selected = value?.id ?? value;
                      return (
                        !value ||
                        option.id.toString() === selected?.toString()
                      );
                    }}
                    renderInput={(params) => {
                      return (
                        <TextField
                          {...params}
                          label="Tipo"
                          margin="normal"
                          variant="outlined"
                          error={!!errors.role}
                          helperText={errors.role?.message?.toString()}
                          required
                          autoFocus
                        />
                      );
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              {...register("username", { required: "Usuario es obligatorio" })}
              error={!!errors?.username}
              helperText={errors.username?.message?.toString()}
              margin="normal"
              required
              fullWidth
              id="username"
              label="Usuario"
              name="username"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              {...register("email", {
                required: "Correo es obligatorio",
                validate: (value) => {
                  return value?.includes("@") || "Correo no válido";
                },
              })}
              error={!!errors?.email}
              helperText={errors.email?.message?.toString()}
              margin="normal"
              fullWidth
              id="email"
              label="Correo"
              name="email"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              {...register("password", {
                minLength: {
                  value: 5,
                  message: "Contraseña debe tener al menos 5 caracteres",
                },
              })}
              id="password"
              margin="normal"
              fullWidth
              name="password"
              label={"Nueva contraseña"}
              helperText={
                errors?.password?.message?.toString() ||
                "Déjala vacía si no quieres cambiarla"
              }
              error={!!errors.password}
              type="password"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              {...register("repeat_password", {
                minLength: {
                  value: 5,
                  message: "Contraseña debe tener al menos 5 caracteres",
                },
                validate: (value) => {
                  const password = getValues("password");
                  if (!password && !value) return true;
                  return value === password || "Contraseñas no coinciden";
                },
              })}
              id="repeat_password"
              margin="normal"
              fullWidth
              name="repeat_password"
              label={"Repetir contraseña"}
              helperText={errors?.repeat_password?.message?.toString()}
              error={!!errors.repeat_password}
              type="password"
            />
          </Grid>
        </Grid>
      </Box>
    </Edit>
  );
};
